<template>
  <div class="review-lhb h-100 d-flex flex-column bg-main">
    <!-- Top Compact Header & Stepper Toolbar -->
    <div class="px-2 px-md-3 py-1 border-bottom bg-card flex-shrink-0" style="z-index: 10;">
      <div class="lhb-max-wrap d-flex align-items-center justify-content-between flex-wrap" style="gap: 6px;">
        <!-- Left: Title & Quick Date Stepper -->
        <div class="d-flex align-items-center flex-nowrap" style="gap: 8px;">
          <span class="font-weight-bold text-main" style="font-size: 1.05rem; white-space: nowrap; letter-spacing: -0.01em;">
            龙虎榜
          </span>

          <!-- Date Stepper: Prev, DatePicker, Next -->
          <div class="lhb-date-stepper d-flex align-items-center">
            <button 
              type="button" 
              class="lhb-date-nav-btn" 
              title="上一交易日" 
              :disabled="loading"
              @click="stepTradeDate(-1)"
            >
              <b-icon icon="chevron-left" font-scale="0.75"></b-icon>
            </button>
            
            <b-form-datepicker
              v-model="date1"
              locale="zh"
              class="lhb-datepicker border-0 mx-0.5"
              size="sm"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }"
              style="width: 150px;"
              :max="new Date()"
            ></b-form-datepicker>

            <button 
              type="button" 
              class="lhb-date-nav-btn" 
              title="下一交易日" 
              :disabled="loading || isToday"
              @click="stepTradeDate(1)"
            >
              <b-icon icon="chevron-right" font-scale="0.75"></b-icon>
            </button>
          </div>
        </div>

        <!-- Right: View Mode Toggle & Actions -->
        <div class="d-flex align-items-center flex-nowrap" style="gap: 6px;">
          <div class="lhb-mode-toggle">
            <button 
              type="button" 
              class="lhb-mode-btn" 
              :class="{ 'lhb-mode-active': viewMode === 'list' }" 
              @click="viewMode = 'list'"
            >
              <b-icon icon="table" class="mr-1" font-scale="0.8"></b-icon>列表
            </button>
            <button 
              type="button" 
              class="lhb-mode-btn" 
              :class="{ 'lhb-mode-active': viewMode === 'mindmap' }" 
              @click="viewMode = 'mindmap'"
            >
              <b-icon icon="diagram-3" class="mr-1" font-scale="0.8"></b-icon>脑图
            </button>
          </div>

          <button 
            type="button" 
            @click="fetchList" 
            class="lhb-action-icon-btn" 
            title="刷新数据"
          >
            <b-icon icon="arrow-clockwise" font-scale="0.85" :animation="loading ? 'spin' : ''"></b-icon>
          </button>

          <WxShare :title="shareTitle" :desc="shareDesc" :link="shareLink" />
        </div>
      </div>
    </div>

    <!-- Seat Filter Bar (Ultra-Compact Horizontal Scrolling / Wrap) -->
    <div v-if="allSeats.length > 0" class="seat-filter-bar px-2 px-md-3 py-1 bg-card border-bottom flex-shrink-0">
      <div class="lhb-max-wrap d-flex align-items-center justify-content-between flex-nowrap" style="gap: 8px;">
        <!-- Left: Filter label + Horizontal Scroll Chips on Mobile, Wrap on PC -->
        <div class="d-flex align-items-center overflow-hidden flex-grow-1">
          <span class="seat-filter-label text-muted font-weight-bold flex-shrink-0 mr-1.5" style="font-size: 11px;">
            席位:
          </span>
          <div class="seat-chips-scroll d-flex align-items-center">
            <button 
              v-for="seat in allSeats" 
              :key="seat.value"
              type="button"
              class="seat-mini-chip-btn"
              :class="{'seat-mini-chip-active': isSeatSelected(seat.value)}"
              @click="toggleSeat(seat.value)"
            >
              <span class="seat-name-text">{{ isMobile && seat.value.length > 4 ? seat.value.substring(0, 4) : seat.value }}</span>
              <span v-if="!showBuySellCounts || isMobile" class="seat-num-text" :class="seat.buyCount > seat.sellCount ? 'text-danger font-weight-bold' : ''">
                ({{ seat.totalCount }})
              </span>
              <span v-else class="seat-num-text">
                (<span class="text-danger font-weight-bold">{{ seat.buyCount }}</span>/<span class="text-success font-weight-bold">{{ seat.sellCount }}</span>)
              </span>
            </button>
          </div>
        </div>

        <!-- Right: Actions -->
        <div class="d-flex align-items-center flex-shrink-0" style="gap: 6px;">
          <b-form-checkbox 
            v-if="!isMobile" 
            v-model="showBuySellCounts" 
            switch 
            size="sm" 
            class="mb-0 custom-switch-mini"
          >
            <span class="small text-muted" style="font-size: 11px;">买卖数</span>
          </b-form-checkbox>

          <button 
            type="button" 
            class="seat-text-btn" 
            @click="toggleAll(!allSelected)"
          >
            {{ allSelected ? '全不选' : '全选' }} ({{ selectedSeats.length }}/{{ allSeats.length }})
          </button>

          <button 
            type="button" 
            class="seat-text-btn d-none d-sm-inline-block text-primary" 
            @click.stop="downloadTable"
            title="导出为 CSV"
          >
            <b-icon icon="download" font-scale="0.8" class="mr-0.5"></b-icon>下载
          </button>
        </div>
      </div>
    </div>

    <!-- Summary Ticker Bar (Single-Line Compact KPI) -->
    <div v-if="statistics && viewMode === 'list'" class="lhb-ticker-bar px-2 px-md-3 py-1 bg-muted border-bottom flex-shrink-0">
      <div class="lhb-max-wrap ticker-scroll-content d-flex align-items-center">
        <div class="ticker-item">
          <span class="ticker-label">净买入累计:</span>
          <strong class="font-numeric ml-1" :class="getColorClass(statistics.netBuy)">
            {{ statistics.netBuy > 0 ? '+' : '' }}{{ formatMoney(statistics.netBuy) }}
          </strong>
        </div>
        <span class="ticker-sep">|</span>
        <div class="ticker-item">
          <span class="ticker-label">成交额(均/总):</span>
          <span class="font-numeric text-main ml-1">{{ formatMoney(statistics.avgTurnover) }}</span>
          <span class="mx-0.5 text-muted">/</span>
          <span class="font-numeric text-main">{{ formatMoney(statistics.turnover) }}</span>
        </div>
        <span class="ticker-sep">|</span>
        <div class="ticker-item">
          <span class="ticker-label">流通市值(均/总):</span>
          <span class="font-numeric text-main ml-1">{{ formatMoney(statistics.avgCircCap) }}</span>
          <span class="mx-0.5 text-muted">/</span>
          <span class="font-numeric text-main">{{ formatMoney(statistics.circCap) }}</span>
        </div>
        <span class="ticker-sep">|</span>
        <div class="ticker-item">
          <span class="ticker-label">总市值(均/总):</span>
          <span class="font-numeric text-main ml-1">{{ formatMoney(statistics.avgTotalCap) }}</span>
          <span class="mx-0.5 text-muted">/</span>
          <span class="font-numeric text-main">{{ formatMoney(statistics.totalCap) }}</span>
        </div>
        <span class="ticker-sep">|</span>
        <div class="ticker-item">
          <span class="ticker-label">上榜:</span>
          <strong class="font-numeric text-main ml-1">{{ filteredLhbList.length }}</strong>
          <span>只</span>
        </div>
      </div>
    </div>

    <!-- Main View Area -->
    <div class="flex-grow-1 d-flex position-relative" :class="{'overflow-auto': viewMode === 'mindmap', 'overflow-hidden': viewMode !== 'mindmap'}">
      <!-- List View -->
      <div 
        v-if="viewMode === 'list'"
        class="list-panel h-100 overflow-auto bg-main w-100 py-1 px-1 px-md-3" 
      >
        <div class="lhb-max-wrap min-h-100">
          <!-- Main Table Card -->
          <div class="lhb-table-card">
            <div class="table-scroll-wrapper">
              <div class="table-scroll-content">
                <!-- List Header -->
                <div class="d-flex lhb-table-header sticky-top" style="z-index: 5; top: 0;">
                  <div :style="getColStyle('name')" class="col-sticky-name">股票名称</div>
                  <div :style="getColStyle('seats')">交易席位</div>
                  <div :style="getColStyle('concepts')">核心题材/概念</div>
                  <div :style="getColStyle('gain')" class="text-right sortable" @click="toggleSort('gain')">
                    涨跌幅
                    <b-icon v-if="sortKey === 'gain'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8" class="sort-icon-active"></b-icon>
                  </div>
                  <div :style="getColStyle('netbuy')" class="text-right sortable" @click="toggleSort('netbuy')">
                    席位净买入
                    <b-icon v-if="sortKey === 'netbuy'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8" class="sort-icon-active"></b-icon>
                  </div>
                  <div :style="getColStyle('turnover')" class="text-right sortable" @click="toggleSort('turnover')">
                    成交额
                    <b-icon v-if="sortKey === 'turnover'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8" class="sort-icon-active"></b-icon>
                  </div>
                  <div :style="getColStyle('total_cap')" class="text-right sortable" @click="toggleSort('total_cap')">
                    总市值
                    <b-icon v-if="sortKey === 'total_cap'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8" class="sort-icon-active"></b-icon>
                  </div>
                  <div :style="getColStyle('circ_cap')" class="text-right sortable" @click="toggleSort('circ_cap')">
                    流通市值
                    <b-icon v-if="sortKey === 'circ_cap'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8" class="sort-icon-active"></b-icon>
                  </div>
                  <div :style="getColStyle('amp')" class="text-right sortable" @click="toggleSort('amp')">
                    振幅
                    <b-icon v-if="sortKey === 'amp'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8" class="sort-icon-active"></b-icon>
                  </div>
                  <div :style="getColStyle('rate')" class="text-right sortable" @click="toggleSort('rate')">
                    换手率
                    <b-icon v-if="sortKey === 'rate'" :icon="sortOrder === 'asc' ? 'caret-up-fill' : 'caret-down-fill'" font-scale="0.8" class="sort-icon-active"></b-icon>
                  </div>
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="text-center py-4">
                  <b-spinner small variant="primary" class="mr-2"></b-spinner>
                  <span class="text-muted small">加载龙虎榜数据中...</span>
                </div>

                <!-- Empty State -->
                <div v-else-if="filteredLhbList.length === 0" class="text-center py-4 text-muted">
                  <b-icon icon="inbox" font-scale="1.5" class="mb-1 d-block mx-auto text-secondary" style="opacity: 0.5;"></b-icon>
                  <span class="small">当前日期暂无龙虎榜上榜数据</span>
                </div>
                
                <!-- List Rows -->
                <div v-else>
                  <div 
                    v-for="(item, rIdx) in filteredLhbList" 
                    :key="item.stock_code" 
                    class="lhb-row-item cursor-pointer"
                    :class="{
                      'lhb-row-selected': selectedStock && selectedStock.stock_code === item.stock_code,
                      'lhb-row-even': rIdx % 2 === 1
                    }"
                    @click="selectStock(item)"
                  >
                    <div class="d-flex align-items-center">
                      <!-- Col 1: Name / Code (STICKY ON MOBILE) -->
                      <div :style="getColStyle('name')" class="col-sticky-name pr-1">
                        <div class="d-flex align-items-center flex-nowrap">
                          <span class="stock-name-text text-main text-truncate font-weight-bold">
                            {{ item.stock_name }}
                          </span>
                          <span v-if="item.t_type === 1 || item.t_icon" class="badge-mini badge-t ml-1">{{ item.t_icon ? item.t_icon + 'T' : 'T' }}</span>
                        </div>
                        <div class="d-flex align-items-center flex-nowrap mt-n0.5">
                          <span class="stock-code-text font-numeric text-muted">{{ item.stock_code }}</span>
                          <span v-if="item.d3 > 0" class="badge-mini badge-3d ml-1">3日</span>
                        </div>
                      </div>

                      <!-- Col 2: Trading Seats -->
                      <div :style="getColStyle('seats')" class="pr-1">
                        <div class="d-flex flex-wrap align-items-center seat-pills-row" style="gap: 2px;">
                          <span 
                            v-for="(icon, idx) in item.buy_group_icons" 
                            :key="'buy'+idx" 
                            class="seat-mini-chip seat-chip-buy"
                            :title="`${icon.name}: 买入 +${formatMoney(icon.amount)}`"
                          >
                            {{ icon.name }} +{{ formatMoney(icon.amount) }}
                          </span>
                          <span 
                            v-for="(icon, idx) in item.sell_group_icons" 
                            :key="'sell'+idx" 
                            class="seat-mini-chip seat-chip-sell"
                            :title="`${icon.name}: 卖出 -${formatMoney(icon.amount)}`"
                          >
                            {{ icon.name }} -{{ formatMoney(icon.amount) }}
                          </span>
                        </div>
                      </div>

                      <!-- Col 3: Concepts -->
                      <div :style="getColStyle('concepts')" class="pr-1">
                        <div class="d-flex flex-wrap align-items-center concepts-pills-row" style="gap: 2px;">
                          <span 
                            v-for="(concept, idx) in getDisplayConcepts(item.concepts)" 
                            :key="'c'+idx" 
                            class="concept-mini-pill text-truncate"
                            :title="concept"
                          >
                            {{ concept }}
                          </span>
                        </div>
                      </div>

                      <!-- Col 4: Increase & Ladder Status -->
                      <div :style="getColStyle('gain')" class="text-right pr-1">
                        <div class="font-numeric font-weight-bold quote-gain-num" :class="getColorClass(item.quote_change)">
                          {{ item.quote_change > 0 ? '+' : '' }}{{ item.quote_change }}%
                        </div>
                        <div v-if="item.up_desc">
                          <span 
                            class="ladder-mini-badge" 
                            :class="getLadderClass(item.up_desc)"
                            :title="item.up_desc"
                          >
                            {{ item.up_desc }}
                          </span>
                        </div>
                      </div>

                      <!-- Col 5: Net Buy -->
                      <div :style="getColStyle('netbuy')" class="text-right pr-1">
                        <div class="font-numeric font-weight-bold quote-netbuy-num" :class="getColorClass(item.buy_in)">
                          {{ item.buy_in > 0 ? '+' : '' }}{{ formatMoney(item.buy_in) }}
                        </div>
                      </div>

                      <!-- Col 6: Turnover -->
                      <div :style="getColStyle('turnover')" class="text-right pr-1 font-numeric text-main font-weight-medium small-val">
                        {{ formatMoney(item.turnover) }}
                      </div>

                      <!-- Col 7: Total Cap -->
                      <div :style="getColStyle('total_cap')" class="text-right pr-1 font-numeric text-muted small-val">
                        {{ formatMoney(item.capitalization) }}
                      </div>

                      <!-- Col 8: Circ Cap -->
                      <div :style="getColStyle('circ_cap')" class="text-right pr-1 font-numeric text-muted small-val">
                        {{ formatMoney(item.circ_price) }}
                      </div>

                      <!-- Col 9: Amplitude -->
                      <div :style="getColStyle('amp')" class="text-right pr-1 font-numeric text-main small-val">
                        {{ item.amplitude }}%
                      </div>

                      <!-- Col 10: Turnover Ratio -->
                      <div :style="getColStyle('rate')" class="text-right pr-1 font-numeric text-main small-val">
                        {{ item.turnover_ratio }}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <!-- Mind Map View -->
      <div v-if="viewMode === 'mindmap'" class="w-100 bg-card p-2">

         <div class="d-flex justify-content-end mb-2 align-items-center">
             <div class="mr-3 text-muted small">说明：脑图可以拖动和放大缩小，调整合适再下载。</div>
             <b-form-radio-group
                v-model="chartType"
                :options="[{text: '树状图', value: 'tree'}, {text: '桑基图', value: 'sankey'}]"
                buttons
                button-variant="outline-primary"
                size="sm"
             ></b-form-radio-group>
         </div>
         <v-chart v-if="currentOption" :options="currentOption" autoresize :style="mindMapContainerStyle" />
         <div v-else class="text-center p-5 text-muted">暂无数据</div>
      </div>

      <!-- Detail Modal -->
      <b-modal v-model="showDetailModal" size="xl" hide-footer centered body-class="p-0">
         <template #modal-title>
             <div v-if="selectedStock" class="d-flex align-items-center">
                 <span class="mr-2 font-weight-bold">{{ selectedStock.stock_name }}</span>
                 <span class="text-secondary small">{{ selectedStock.stock_code }}</span>
             </div>
             <div v-else>详情</div>
         </template>
         <div style="height: 70vh; overflow-y: auto;">
            <detail-content 
                v-if="selectedStock" 
                :detail-data="detailData" 
                :loading="detailLoading" 
                :stock="selectedStock" 
                :date="date1"
                :is-mobile="isMobile"
                @close="showDetailModal = false"
            ></detail-content>
         </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import DetailContent from './LhbDetailContent.vue'
import WxShare from '@/components/share/WxShare.vue'

export default {
  components: { DetailContent, WxShare },
  data() {
    return {
      reqId: 0,
      date1: new Date().toISOString().slice(0, 10),
      lhbList: [],
      selectedStock: null,
      showDetailModal: false,
      detailLoading: false,
      detailData: null,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      loading: false,
      viewMode: 'list', // 'list' or 'mindmap'
      chartType: 'tree', // 'tree' or 'sankey'
      selectedSeats: [],
      showBuySellCounts: false,
      sortKey: 'gain',
      sortOrder: 'desc',
      seatCategoryMap: {
        '机构专用': '机构',
        '深股通专用': '北向',
        '沪股通专用': '北向'
      }
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768
    },
    isToday() {
      const today = new Date().toISOString().slice(0, 10)
      return this.date1 >= today
    },
    filterStyle() {
      if (this.viewMode === 'list' && !this.isMobile) {
        return 'max-width: 1560px; margin: 0 auto; width: 100%;'
      }
      return 'width: 100%;'
    },
    allSeats() {
      if (!this.lhbList || this.lhbList.length === 0) return []
      const stats = {}
      
      this.lhbList.forEach(item => {
        const stockSeatNet = {} // seatName -> netAmount

        if (item.buy_group_icons) {
          item.buy_group_icons.forEach(i => {
            if (i.name) {
              if (!stockSeatNet[i.name]) stockSeatNet[i.name] = 0
              stockSeatNet[i.name] += Number(i.amount || 0)
            }
          })
        }
        
        if (item.sell_group_icons) {
          item.sell_group_icons.forEach(i => {
            if (i.name) {
              if (!stockSeatNet[i.name]) stockSeatNet[i.name] = 0
              stockSeatNet[i.name] -= Number(i.amount || 0)
            }
          })
        }
        
        // Aggregate to global stats
        Object.keys(stockSeatNet).forEach(name => {
          if (!stats[name]) stats[name] = { buy: 0, sell: 0 }
          const net = stockSeatNet[name]
          if (net > 0) {
            stats[name].buy += 1
          } else if (net < 0) {
            stats[name].sell += 1
          }
        })
      })
      
      const sorted = Object.keys(stats).sort()
      return sorted.map(name => {
        let text = name
        if (this.isMobile && name.length > 4) {
          text = name.substring(0, 4)
        }
        
        const s = stats[name]
        const total = s.buy + s.sell
        let totalHtml = total
        if (s.buy > s.sell) {
          totalHtml = `<span class="text-danger font-weight-bold">${total}</span>`
        }
        // Construct HTML
        let html
        if (this.isMobile || !this.showBuySellCounts) {
          html = `${text}(${totalHtml})`
        } else {
          html = `${text} <span class="text-danger font-weight-bold">${s.buy}</span>+<span class="text-success font-weight-bold">${s.sell}</span>=${totalHtml}`
        }
        
        return {
          name: text,
          value: name,
          buyCount: s.buy,
          sellCount: s.sell,
          totalCount: total,
          html
        }
      })
    },
    allSelected() {
        return this.selectedSeats.length === this.allSeats.length
    },
    indeterminate() {
        return this.selectedSeats.length > 0 && this.selectedSeats.length < this.allSeats.length
    },
    filteredLhbList() {
      if (!this.lhbList) return []
      if (this.allSeats && this.allSeats.length > 0 && this.selectedSeats.length === 0) return []
      // If all selected or no seats filter, return all (optimization)
      let base = this.lhbList
      if (this.allSeats && this.allSeats.length > 0 && this.selectedSeats.length !== this.allSeats.length) {
        const selectedSet = new Set(this.selectedSeats)
        base = this.lhbList.filter(item => {
          const itemSeats = []
          if (item.buy_group_icons) item.buy_group_icons.forEach(i => i.name && itemSeats.push(i.name))
          if (item.sell_group_icons) item.sell_group_icons.forEach(i => i.name && itemSeats.push(i.name))
          return itemSeats.some(name => selectedSet.has(name))
        })
      }
      if (!this.sortKey) return base
      const key = this.sortKey
      const dir = this.sortOrder === 'asc' ? 1 : -1
      return base.slice().sort((a, b) => {
        const va = this.getSortValue(a, key)
        const vb = this.getSortValue(b, key)
        if (va === vb) return 0
        return va > vb ? dir : -dir
      })
    },
    statistics() {
      if (!this.filteredLhbList || this.filteredLhbList.length === 0) return null;
      
      let totalNetBuy = 0;
      let totalTurnover = 0;
      let totalCap = 0;
      let totalCircCap = 0;
      const count = this.filteredLhbList.length;

      this.filteredLhbList.forEach(item => {
        totalNetBuy += Number(item.buy_in || 0);
        totalTurnover += Number(item.turnover || 0);
        totalCap += Number(item.capitalization || 0);
        totalCircCap += Number(item.circ_price || 0);
      });

      return {
        netBuy: totalNetBuy,
        turnover: totalTurnover,
        totalCap: totalCap,
        circCap: totalCircCap,
        avgTurnover: count > 0 ? totalTurnover / count : 0,
        avgTotalCap: count > 0 ? totalCap / count : 0,
        avgCircCap: count > 0 ? totalCircCap / count : 0
      };
    },
    mindMapContainerStyle() {
      // Responsive width: Mobile 98%, PC 90%
      const w = this.isMobile ? '98%' : '85%'

      if (this.chartType === 'sankey') {
         const h = this.getSankeyHeight()
         return { width: w, height: h + 'px' }
      }
      
      const h = this.getTreeHeight()
      return { width: w, height: h + 'px' }
    },
    currentOption() {
      if (this.chartType === 'sankey') {
        return this.sankeyOption
      }
      return this.treeOption
    },
    sankeyOption() {
      if (!this.filteredLhbList || this.filteredLhbList.length === 0) return null
      
      const links = []
      const usedNodes = new Set()
      const stockNames = new Set(this.filteredLhbList.map(s => s.stock_name))
      
      this.filteredLhbList.forEach(stock => {
          // Process Seats
          const processSeat = (seats, type) => {
              if (!seats) return
              seats.forEach(seat => {
                  if (!seat || !seat.name) return
                  
                  const displayName = this.getSeatDisplayName(seat.name)

                  // Valid link found
                  usedNodes.add(displayName)
                  usedNodes.add(stock.stock_name)
                  
                  // Link
                  // Simplified: Always Seat -> Stock
                  // Color: Red for Buy, Green for Sell
                  // Value: Amount
                  links.push({
                      source: displayName,
                      target: stock.stock_name,
                      value: Math.abs(seat.amount),
                      lineStyle: {
                          color: type === 'buy' ? '#ff4d4f' : '#52c41a',
                          opacity: 0.4
                      },
                      tooltip: {
                          formatter: `${displayName} ${type === 'buy'?'买入':'卖出'} ${stock.stock_name}<br/>金额: ${this.formatMoney(Math.abs(seat.amount))}`
                      }
                  })
              })
          }
          
          processSeat(stock.buy_group_icons, 'buy')
          processSeat(stock.sell_group_icons, 'sell')
      })
      
      if (links.length === 0) return null

      const nodes = Array.from(usedNodes).map(name => {
          return { 
              name: name, 
              itemStyle: { 
                  color: stockNames.has(name) ? '#1890ff' : '#fa8c16' 
              } 
          }
      })
      
      return {
          tooltip: {
              trigger: 'item',
              triggerOn: 'mousemove'
          },
          toolbox: {
            feature: {
              saveAsImage: { show: true, title: '保存为图片' }
            }
          },
          graphic: this.getWatermarks(),
          series: [{
              type: 'sankey',
              data: nodes,
              links: links,
              emphasis: {
                  focus: 'adjacency'
              },
              nodeAlign: 'left',
              layoutIterations: 32, // Improve layout
              lineStyle: {
                  color: 'source',
                  curveness: 0.5
              },
              label: {
                  color: '#333',
                  fontSize: 12
              },
              // Shrink width by increasing padding
              left: '25%',
              right: '35%',
              top: '10%',
              bottom: '10%'
          }]
      }
    },
    treeOption() {
      if (!this.filteredLhbList || this.filteredLhbList.length === 0) return null

      // Group by seat name
      // seatName -> [{stockName, quoteChange, upDesc, buyIn, seatAmount}]
      let seatMap = {}

      this.filteredLhbList.forEach(stock => {
        if (stock.buy_group_icons) {
          stock.buy_group_icons.forEach(icon => {
            if (!seatMap[icon.name]) {
              seatMap[icon.name] = []
            }
            seatMap[icon.name].push({
              stockName: stock.stock_name,
              stockCode: stock.stock_code,
              quoteChange: stock.quote_change,
              upDesc: stock.up_desc,
              buyIn: stock.buy_in,
              seatAmount: icon.amount,
              turnover: stock.turnover,
              buySeats: stock.buy_group_icons || [],
              sellSeats: stock.sell_group_icons || [],
              seatType: 'buy',
              concepts: stock.concepts
            })
          })
        }
        if (stock.sell_group_icons) {
          stock.sell_group_icons.forEach(icon => {
            if (!seatMap[icon.name]) {
              seatMap[icon.name] = []
            }
            seatMap[icon.name].push({
              stockName: stock.stock_name,
              stockCode: stock.stock_code,
              quoteChange: stock.quote_change,
              upDesc: stock.up_desc,
              buyIn: stock.buy_in,
              seatAmount: -Math.abs(icon.amount || 0),
              turnover: stock.turnover,
              buySeats: stock.buy_group_icons || [],
              sellSeats: stock.sell_group_icons || [],
              seatType: 'sell',
              concepts: stock.concepts
            })
          })
        }
      })

      // Calculate Weekday
      const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const dayIndex = new Date(this.date1).getDay()
      const weekDayStr = weekDays[dayIndex]

      // Build children for root
      let children = []

      // Grouping buckets
      const targetCategories = ['一线游资', '顶级游资', '知名游资', '量化']
      const groupedNodes = {}
      targetCategories.forEach(c => groupedNodes[c] = [])
      const otherNodes = []

      for (let seatName in seatMap) {
        let stocks = seatMap[seatName]
        let stockChildren = stocks.map(s => {
          // Format label
          // name: Stock Name
          let stockLabel = `{name|${s.stockName}(${s.stockCode})}`
          const t = Number(s.turnover || 0)
          
          // Helper to build line
          const buildLine = (name, amount, type) => {
             const typeStyle = type === 'buy' ? 'typeBuy' : 'typeSell'
             const typeText = type === 'buy' ? '买' : '卖'
             const amtText = this.formatMoney(Math.abs(amount || 0))
             const pct = (t > 0 && amount) ? (Math.abs(amount) / t * 100).toFixed(1) : '0.0'
             const pctText = `占${pct}%`
             // Net buy moved to info node
             
             // {seat|Name} {type|Buy/Sell} {amt|Amount} {pct|Pct}
             return `{seat|${name}}{${typeStyle}|${typeText}}{amt|${amtText}}{pct|${pctText}}`
          }

          const detailChildren = []
          
          // 1. Add Info Node (Stock Metadata)
          const netColor = s.buyIn > 0 ? '#e33e33' : '#17803d'
          
          // Process concepts
          let conceptsStr = ''
          if (s.concepts) {
            const concepts = s.concepts.split(',').map(c => {
                const parts = c.split(':')
                return parts.length > 1 ? parts[1] : parts[0]
            }).slice(0, 5)
            conceptsStr = concepts.join(' ')
          }
          
          const infoNodeName = `{desc|${s.upDesc || ''}} {val|${s.quoteChange}%} {net|净${this.formatMoney(s.buyIn)}}` + (conceptsStr ? ` {c|${conceptsStr}}` : '')

          detailChildren.push({
              name: infoNodeName,
              value: 0,
              label: {
                  formatter: '{b}',
                  backgroundColor: '#fff0f6', // Pink background
                  borderColor: '#ffadd2',     // Pink border
                  borderWidth: 1,
                  borderRadius: 4,
                  padding: [2, 8],
                  align: 'left',
                  rich: {
                      desc: { fontSize: 11, color: '#666', padding: [0, 10, 0, 0] },
                      val: { fontSize: 12, fontWeight: 'bold', color: s.quoteChange > 0 ? '#e33e33' : '#17803d', padding: [0, 10, 0, 0] },
                      net: { fontSize: 11, color: netColor, fontWeight: 'bold' },
                      c: { fontSize: 11, color: '#666', padding: [0, 0, 0, 10] }
                  }
              }
          })
          
          // Helper to create child node
          const createDetailNode = (name, amount, type) => {
              return {
                  name: buildLine(name, amount, type),
                  value: amount,
                  label: {
                      formatter: '{b}',
                      backgroundColor: '#fff',
                      borderColor: '#eee',
                      borderWidth: 1,
                      borderRadius: 4,
                      padding: [1, 7],
                      align: 'left',
                      rich: {
                          seat: { width: 75, align: 'left', fontSize: 11, fontWeight: 'bold', color: '#333' },
                          typeBuy: { width: 16, align: 'center', color: '#e33e33', fontWeight: 'bold', fontSize: 11 },
                          typeSell: { width: 16, align: 'center', color: '#17803d', fontWeight: 'bold', fontSize: 11 },
                          amt: { width: 60, align: 'right', fontFamily: 'monospace', fontWeight: 'bold', fontSize: 11, color: '#333' },
                          pct: { width: 45, align: 'right', color: '#999', fontSize: 10 }
                      }
                  }
              }
          }

          // Main seat line
          detailChildren.push(createDetailNode(seatName, s.seatAmount, s.seatType))

          if (Array.isArray(s.buySeats)) {
            s.buySeats.forEach(ic => {
              if (!ic || !ic.name) return
              if (ic.name === seatName) return
              detailChildren.push(createDetailNode(ic.name, ic.amount, 'buy'))
            })
          }
          if (Array.isArray(s.sellSeats)) {
            s.sellSeats.forEach(ic => {
              if (!ic || !ic.name) return
              if (ic.name === seatName) return
              detailChildren.push(createDetailNode(ic.name, ic.amount, 'sell'))
            })
          }

          // 3. Add Concepts Node (Last Node)
          // Removed as per request, merged into Info Node

          return {
            name: stockLabel,
            value: s.seatAmount,
            label: {
                formatter: '{b}',
                backgroundColor: '#f8f9fa',
                borderColor: '#ddd',
                borderWidth: 1,
                borderRadius: 4,
                padding: [1, 8],
                align: 'left',
                rich: {
                    name: { fontSize: 12, fontWeight: 'normal', color: '#333' }
                }
            },
            // Add a transparent intermediate node to shorten the line?
            // No, just use children directly but adjust layout
            children: detailChildren
          }
        })
        
        // Group Logic
        const category = this.seatCategoryMap[seatName]
        const seatNode = {
          name: (category && targetCategories.includes(category)) ? seatName : this.getSeatDisplayName(seatName),
          children: stockChildren.sort((a, b) => Math.abs(b.value) - Math.abs(a.value)),
          label: {
              fontWeight: 'bold',
              fontSize: 14
          },
          collapsed: ['量化基金', '量化打板'].includes(seatName)
        }

        if (category && targetCategories.includes(category)) {
            groupedNodes[category].push(seatNode)
        } else {
            otherNodes.push(seatNode)
        }
      }

      // Build Children Array
      // 1. Add Categories
      targetCategories.forEach(cat => {
          const nodes = groupedNodes[cat]
          if (nodes.length > 0) {
              nodes.sort((a, b) => b.children.length - a.children.length)
              children.push({
                  name: cat,
                  children: nodes,
                  label: {
                      fontSize: 16,
                      fontWeight: 'bold',
                      backgroundColor: '#e6f7ff',
                      borderColor: '#1890ff',
                      borderWidth: 1,
                      borderRadius: 4,
                      padding: [4, 8],
                      color: '#000'
                  },
                  collapsed: false
              })
          }
      })

      // 2. Add Others to '其他游资'
      if (otherNodes.length > 0) {
        otherNodes.sort((a, b) => b.children.length - a.children.length)
        children.push({
            name: '其他游资',
            children: otherNodes,
            label: {
                fontSize: 16,
                fontWeight: 'bold',
                backgroundColor: '#e6f7ff',
                borderColor: '#1890ff',
                borderWidth: 1,
                borderRadius: 4,
                padding: [4, 8],
                color: '#000'
            },
            collapsed: false
        })
      }

      return {
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          formatter: (params) => {
              // Simple tooltip for now
              return params.name.replace(/\n/g, '<br/>')
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { show: true, title: '保存为图片' }
          }
        },
        graphic: this.getWatermarks(),
        series: [
          {
            type: 'tree',
            zoom: 1,
            edgeShape: 'polyline',
            edgeForkPosition: '63%',
            nodePadding: 2,
            layerPadding: 20, // Decrease layer padding to shorten lines
            data: [{
              name: `龙虎榜 \n${this.date1 || ''}\n ${weekDayStr}`,
              children: children,
              label: {
                  show: true,
                  fontSize: 15,
                  fontWeight: 'bold',
                  backgroundColor: '#e6f7ff',
                  borderColor: '#1890ff',
                  borderWidth: 1,
                  borderRadius: 4,
                  padding: [4, 8],
                  lineHeight: 20,
                  align: 'center'
              },
              symbolSize: 10
            }],
            top: '2%',
            left: '10%', // Increase left margin for root node
            bottom: '2%',
            right: '25%', // Increase right margin to squeeze width
            symbolSize: 2,
            roam: true, // Enable zoom and pan
            initialTreeDepth: this.isMobile ? 3 : 6,
            label: {
              position: 'left',
              verticalAlign: 'middle',
              align: 'right',
              fontSize: 12
            },
            leaves: {
              label: {
                position: 'right',
                verticalAlign: 'middle',
                align: 'left',
                padding: [2, 2]
              }
            },
            expandAndCollapse: true,
            animationDuration: 550,
            animationDurationUpdate: 750
          }
        ]
      }
    },
    shareTitle() {
      return `龙虎榜分析 (${this.date1}) - 自在量化`
    },
    shareDesc() {
      const count = this.lhbList ? this.lhbList.length : 0
      return `截止 ${this.date1}，共监控到 ${count} 只股票上榜。包含游资/机构买卖席位详情、榜单脑图和多维度资金流向过滤分析。`
    },
    shareLink() {
      const origin = window.location.origin
      return `${origin}/review/uplimit/lhb?date=${this.date1}`
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    if (this.$route.query.date) {
        this.date1 = this.$route.query.date
        this.fetchList()
    } else {
        this.setDefaultTradeDateByRule()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    getSankeyHeight() {
         const winH = this.windowHeight
         // Sankey height needs to be large enough for all nodes
         // Reduce height even more: 15px per node
         const estimated = this.lhbList.length * 15 + 100
         // Limit max height to 0.8 screen height (was 1.5)
         return Math.max(300, Math.min(Math.floor(winH * 0.8), estimated))
    },
    getTreeHeight() {
        let seatSet = new Set()
        this.lhbList.forEach(stock => {
            if (stock.buy_group_icons) {
                stock.buy_group_icons.forEach(icon => {
                    if (icon && icon.name) seatSet.add(icon.name)
                })
            }
            if (stock.sell_group_icons) {
                stock.sell_group_icons.forEach(icon => {
                    if (icon && icon.name) seatSet.add(icon.name)
                })
            }
        })
        
        let totalLines = 0
        this.lhbList.forEach(stock => {
            const n = (stock.buy_group_icons ? stock.buy_group_icons.length : 0) + 
                      (stock.sell_group_icons ? stock.sell_group_icons.length : 0)
            if (n > 0) totalLines += n * n
        })

        const seats = Math.max(1, seatSet.size)
        const winH = this.windowHeight
        
        // Height estimation:
        // seats * gap + totalLines * line_height
        // Compact height to avoid "too big" look
        const estimated = seats * 15 + totalLines * 12
        
        const minH = Math.max(400, Math.floor(winH * 0.5))
        const maxH = Math.floor(winH * 5.0) // Allow very tall trees if needed
        return Math.max(minH, Math.min(maxH, estimated))
    },
    getWatermarks() {
       return [{
           type: 'text',
           z: 100,
           silent: true,
           left: 'center',
           top: 'center',
           rotation: Math.PI/6,
           style: {
               fill: 'rgba(0, 0, 0, 0.1)',
               text: '自在量化\nquant.zizizaizai.com',
               font: 'bold 30px sans-serif',
               textAlign: 'center'
           }
       }]
    },
    onResize() {
        this.windowWidth = window.innerWidth
        this.windowHeight = window.innerHeight
    },
    fetchList() {
      const curReqId = ++this.reqId;
      const curDate = this.date1;
      this.loading = true
      this.selectedSeats = []
      this.seatCategoryMap = {
         '机构专用': '机构',
         '深股通专用': '北向',
         '沪股通专用': '北向'
      }
      request_zzquant({
        url: '/v3/market/lhb/list',
        method: 'get',
        params: {
          date1: this.date1
        }
      }).then(res => {
        if (curReqId !== this.reqId || curDate !== this.date1) return;
        const data = res.data
        if (data.code === 20000) {
          this.lhbList = data.data || []
          
          // Build Seat Category Map dynamically
          this.lhbList.forEach(item => {
             // Buy Seats
             if (item.buy_group_icons) {
                item.buy_group_icons.forEach(seat => {
                    if (seat && seat.name) {
                        if (seat.youzi_icon) {
                            const map = {1: '一线游资', 2: '顶级游资', 3: '知名游资'}
                            this.seatCategoryMap[seat.name] = map[seat.youzi_icon] || ''
                        } else if (seat.tags) {
                            // Check for specific tags
                            const tags = seat.tags.split(',')
                            for(let t of tags) {
                                if (['顶级游资', '一线游资', '知名游资', '量化'].includes(t)) {
                                    this.seatCategoryMap[seat.name] = t
                                    break
                                }
                            }
                        }
                        
                        // Override for Quant
                        if (seat.name.indexOf('量化') !== -1) {
                            this.seatCategoryMap[seat.name] = '量化'
                        }
                    }
                })
             }
             // Sell Seats
             if (item.sell_group_icons) {
                item.sell_group_icons.forEach(seat => {
                    if (seat && seat.name) {
                        if (seat.youzi_icon) {
                            const map = {1: '一线游资', 2: '顶级游资', 3: '知名游资'}
                            this.seatCategoryMap[seat.name] = map[seat.youzi_icon] || ''
                        } else if (seat.tags) {
                            const tags = seat.tags.split(',')
                            for(let t of tags) {
                                if (['顶级游资', '一线游资', '知名游资', '量化'].includes(t)) {
                                    this.seatCategoryMap[seat.name] = t
                                    break
                                }
                            }
                        }
                        
                        // Override for Quant
                        if (seat.name.indexOf('量化') !== -1) {
                            this.seatCategoryMap[seat.name] = '量化'
                        }
                    }
                })
             }
          })
          
        } else {
          this.lhbList = []
          this.$bvToast.toast(data.message || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      }).catch(err => {
        if (curReqId !== this.reqId || curDate !== this.date1) return;
        console.error(err)
        this.lhbList = []
        this.$bvToast.toast('网络请求失败', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      }).finally(() => {
        if (curReqId === this.reqId) {
          this.loading = false
        }
      })
    },
    selectStock(item) {
      this.selectedStock = item
      this.showDetailModal = true
      this.fetchDetail(item.stock_code)
    },
    fetchDetail(stockCode) {
        this.detailLoading = true
        this.detailData = null
        request_zzquant({
            url: '/v3/market/lhb/detail',
            params: {
                date1: this.date1,
                stock_code: stockCode
            }
        }).then(res => {
            this.detailLoading = false
            if (res.data.code === 20000) {
                this.detailData = res.data.data
            }
        }).catch(() => {
            this.detailLoading = false
        })
    },
    setDefaultTradeDateByRule() {
        const now = new Date()
        const today = new Date(now.getTime())
        // 交易所龙虎榜通常在收盘后 17:30~18:00 左右公布并入库
        // 18:00 之前默认定位到已有数据的上一交易日；18:00 之后默认定位到今日
        const targetDate = now.getHours() < 18
            ? new Date(now.getTime() - 24 * 3600 * 1000)
            : today
        const dayEnd = targetDate.toISOString().slice(0, 10)
        request_zzquant({
            url: '/v3/market/trade/days',
            params: {
                days: 1,
                day_end: dayEnd
            }
        }).then(res => {
            if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
                this.date1 = res.data.data[0]
            } else {
                this.date1 = dayEnd
            }
            this.fetchList()
        }).catch(() => {
            this.date1 = dayEnd
            this.fetchList()
        })
    },
    getDisplayConcepts(conceptsStr) {
        if (!conceptsStr) return []
        // conceptsStr is "ID:Name,ID:Name"
        let parts = conceptsStr.split(',')
        let display = []
        for (let i = 0; i < parts.length && i < 2; i++) { // Limit to 2
            let p = parts[i]
            let items = p.split(':')
            if (items.length > 1) {
                display.push(items[1]) // Name
            } else {
                display.push(p) // Fallback
            }
        }
        return display
    },
    parseConcept(reason) {
        if (!reason) return '--'
        // Extract readable concepts from strict strings like "日涨幅偏离值达7%的证券"
        // If it contains specific keywords, return simplified version
        let r = reason
        if (r.indexOf('日涨幅') !== -1) return '日涨幅偏离'
        if (r.indexOf('日跌幅') !== -1) return '日跌幅偏离'
        if (r.indexOf('换手率') !== -1) return '高换手'
        if (r.indexOf('振幅') !== -1) return '高振幅'
        
        // If it's a long string, just take first few chars or split
        if (r.length > 6) return r.substring(0, 6)
        
        return r
    },
    getColorClass(val) {
        if (val > 0) return 'text-danger'
        if (val < 0) return 'text-success'
        return ''
    },
    isSeatSelected(name) {
      return this.selectedSeats.includes(name)
    },
    toggleSeat(name) {
      const idx = this.selectedSeats.indexOf(name)
      if (idx > -1) {
        this.selectedSeats.splice(idx, 1)
      } else {
        this.selectedSeats.push(name)
      }
    },
    stepTradeDate(direction) {
      if (this.loading) return
      if (direction === -1) {
        const cur = new Date(this.date1)
        const prevDay = new Date(cur.getTime() - 24 * 3600 * 1000).toISOString().slice(0, 10)
        this.loading = true
        request_zzquant({
          url: '/v3/market/trade/days',
          params: { days: 1, day_end: prevDay }
        }).then(res => {
          if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
            this.date1 = res.data.data[0]
          } else {
            this.loading = false
          }
        }).catch(() => {
          this.loading = false
        })
      } else if (direction === 1) {
        const cur = new Date(this.date1)
        const nextDay = new Date(cur.getTime() + 24 * 3600 * 1000).toISOString().slice(0, 10)
        const today = new Date().toISOString().slice(0, 10)
        if (nextDay > today) return
        this.loading = true
        request_zzquant({
          url: '/v3/market/trade/days',
          params: { day_start: nextDay, day_end: today }
        }).then(res => {
          if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
            this.date1 = res.data.data[0]
          } else {
            this.loading = false
          }
        }).catch(() => {
          this.loading = false
        })
      }
    },
    getLadderClass(desc) {
      if (!desc) return ''
      if (desc.includes('连板')) {
        const num = parseInt(desc) || 0
        if (num >= 5) return 'ladder-high'
        if (num >= 3) return 'ladder-mid'
        return 'ladder-multi'
      }
      if (desc.includes('首板')) return 'ladder-first'
      if (desc.includes('包') || desc.includes('反')) return 'ladder-rebound'
      return 'ladder-default'
    },
    getColStyle(field) {
      if (!this.isMobile) {
        const pcStyles = {
          name: 'flex: 0 0 115px; width: 115px;',
          seats: 'flex: 1 1 200px; min-width: 170px;',
          concepts: 'flex: 1 1 140px; min-width: 130px;',
          gain: 'flex: 0 0 85px; width: 85px;',
          netbuy: 'flex: 0 0 95px; width: 95px;',
          turnover: 'flex: 0 0 80px; width: 80px;',
          total_cap: 'flex: 0 0 80px; width: 80px;',
          circ_cap: 'flex: 0 0 80px; width: 80px;',
          amp: 'flex: 0 0 65px; width: 65px;',
          rate: 'flex: 0 0 65px; width: 65px;'
        }
        return pcStyles[field] || ''
      } else {
        const mobileWidths = {
          name: 'flex: 0 0 95px; width: 95px; min-width: 95px;',
          seats: 'flex: 0 0 150px; width: 150px; min-width: 150px;',
          concepts: 'flex: 0 0 110px; width: 110px; min-width: 110px;',
          gain: 'flex: 0 0 75px; width: 75px; min-width: 75px;',
          netbuy: 'flex: 0 0 85px; width: 85px; min-width: 85px;',
          turnover: 'flex: 0 0 75px; width: 75px; min-width: 75px;',
          total_cap: 'flex: 0 0 75px; width: 75px; min-width: 75px;',
          circ_cap: 'flex: 0 0 75px; width: 75px; min-width: 75px;',
          amp: 'flex: 0 0 60px; width: 60px; min-width: 60px;',
          rate: 'flex: 0 0 60px; width: 60px; min-width: 60px;'
        }
        return mobileWidths[field] || ''
      }
    },
    getSeatDisplayName(name) {
        const category = this.seatCategoryMap[name]
        if (category) {
            return `${category}·${name}`
        }
        return name
    },
    formatMoney(val) {
        if (!val) return '0'
        let absVal = Math.abs(val)
        let res = ''
        if (absVal >= 100000000) {
            res = (absVal / 100000000).toFixed(2) + '亿'
        } else if (absVal >= 10000) {
            res = (absVal / 10000000).toFixed(2) + '千万'
        } else {
            res = absVal.toFixed(2)
        }
        if (val < 0) return '-' + res
        return res
    },
    toggleAll(checked) {
      this.selectedSeats = checked ? this.allSeats.map(i => i.value) : []
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
    },
    getSortValue(item, key) {
      const map = {
        gain: 'quote_change',
        netbuy: 'buy_in',
        turnover: 'turnover',
        total_cap: 'capitalization',
        circ_cap: 'circ_price',
        amp: 'amplitude',
        rate: 'turnover_ratio'
      }
      const field = map[key] || key
      const raw = item && item[field] != null ? item[field] : 0
      const num = Number(raw)
      return Number.isNaN(num) ? 0 : num
    },
    downloadTable() {
      const rows = this.filteredLhbList || []
      const header = ['股票名称','股票代码','涨幅','净买入','成交额','总市值','流通市值','振幅','换手率','交易席位','概念']
      const lines = [header.join(',')]
      rows.forEach(item => {
        let concepts = ''
        if (Array.isArray(item.concepts)) {
          concepts = item.concepts.join('|')
        } else if (item.concepts) {
          const parts = String(item.concepts).split(',')
          concepts = parts.map(p => {
            const seg = p.split(':')
            return seg.length > 1 ? seg.slice(1).join(':') : seg[0]
          }).join('|')
        }
        const buySeats = Array.isArray(item.buy_group_icons)
          ? item.buy_group_icons.map(i => `${i.name || ''}(+${this.formatMoney(i.amount)})`).filter(v => v && v !== '(+0)').join('|')
          : ''
        const sellSeats = Array.isArray(item.sell_group_icons)
          ? item.sell_group_icons.map(i => `${i.name || ''}(-${this.formatMoney(i.amount)})`).filter(v => v && v !== '(-0)').join('|')
          : ''
        const seats = `买:${buySeats} 卖:${sellSeats}`.trim()
        const vals = [
          item.stock_name || '',
          item.stock_code || '',
          item.quote_change != null ? `${item.quote_change}%` : '',
          this.formatMoney(item.buy_in),
          this.formatMoney(item.turnover),
          this.formatMoney(item.capitalization),
          this.formatMoney(item.circ_price),
          item.amplitude != null ? `${item.amplitude}%` : '',
          item.turnover_ratio != null ? `${item.turnover_ratio}%` : '',
          seats,
          concepts
        ]
        const escaped = vals.map(v => {
          const s = String(v).replace(/"/g, '""')
          return `"${s}"`
        })
        lines.push(escaped.join(','))
      })
      const csv = '\uFEFF' + lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const fileName = `龙虎榜_${this.date1 || ''}.csv`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      link.click()
      URL.revokeObjectURL(link.href)
    }
  },
  watch: {
      date1() {
          this.fetchList()
      },
      allSeats(val, oldVal) {
          // Helper to get signature of values
          const getSig = (arr) => {
              if (!arr) return ''
              return arr.map(i => i.value).sort().join('|')
          }
          // Only update if values changed (ignore text changes due to resize)
          if (getSig(val) !== getSig(oldVal)) {
              this.selectedSeats = val.map(i => i.value)
          }
      }
  }
}
</script>

<style scoped>
/* Base Theme & Utilities */
.review-lhb {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--text-main);
  background-color: var(--bg-main);
}

.text-danger { color: var(--color-up-color, #ef4444) !important; }
.text-success { color: var(--color-down-color, #22c55e) !important; }
.text-main { color: var(--text-main) !important; }
.cursor-pointer { cursor: pointer; }

.font-numeric {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

/* Toolbar & Date Stepper */
.lhb-date-stepper {
  background: var(--bg-muted);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 1px 3px;
  display: inline-flex;
  align-items: center;
}

.lhb-date-nav-btn {
  border: none;
  background: transparent;
  color: var(--text-muted);
  width: 22px;
  height: 22px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.12s ease;
  cursor: pointer;
}

.lhb-date-nav-btn:hover:not(:disabled) {
  background: var(--bg-card);
  color: var(--text-main);
}

.lhb-date-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.lhb-datepicker >>> .btn {
  background: transparent !important;
  border: none !important;
  font-size: 0.8rem !important;
  font-weight: 500 !important;
  color: var(--text-main) !important;
  padding: 1px 4px !important;
  box-shadow: none !important;
}

.lhb-mode-toggle {
  display: inline-flex;
  background: var(--bg-muted);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 1px;
}

.lhb-mode-btn {
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.12s ease;
  display: inline-flex;
  align-items: center;
}

.lhb-mode-btn:hover {
  color: var(--text-main);
}

.lhb-mode-active {
  background: var(--bg-card) !important;
  color: var(--accent-color, #3b82f6) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  font-weight: 600;
}

.lhb-action-icon-btn {
  border-radius: 6px;
  height: 24px;
  width: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  background: var(--bg-card);
  cursor: pointer;
}

.lhb-action-icon-btn:hover {
  color: var(--text-main);
  border-color: var(--accent-color);
}

/* Seat Filter Bar (Ultra-Compact) */
.seat-filter-bar {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-color);
  font-size: 11.5px;
}

.seat-chips-scroll {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.seat-chips-scroll::-webkit-scrollbar {
  display: none;
}

@media (min-width: 768px) {
  .seat-chips-scroll {
    flex-wrap: wrap;
  }
}

.seat-mini-chip-btn {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background: var(--bg-muted);
  color: var(--text-muted);
  font-size: 11px;
  line-height: 18px;
  cursor: pointer;
  user-select: none;
  transition: all 0.12s ease;
  flex-shrink: 0;
}

.seat-mini-chip-btn:hover {
  border-color: var(--accent-color);
  color: var(--text-main);
}

.seat-mini-chip-active {
  background: rgba(59, 130, 246, 0.14) !important;
  border-color: var(--accent-color, #3b82f6) !important;
  color: var(--accent-color, #3b82f6) !important;
  font-weight: 500;
}

.seat-num-text {
  margin-left: 2px;
  font-size: 10.5px;
}

.seat-text-btn {
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  color: var(--text-muted);
  cursor: pointer;
  white-space: nowrap;
  line-height: 18px;
}

.seat-text-btn:hover {
  color: var(--text-main);
  border-color: var(--accent-color);
}

.custom-switch-mini >>> .custom-control-label {
  cursor: pointer;
  padding-top: 1px;
}

/* Single-Line Summary Ticker Bar */
.lhb-ticker-bar {
  background: var(--bg-muted);
  border-bottom: 1px solid var(--border-color);
  font-size: 11.5px;
  line-height: 20px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.lhb-ticker-bar::-webkit-scrollbar {
  display: none;
}

.ticker-scroll-content {
  gap: 6px;
  white-space: nowrap;
}

.ticker-item {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
}

.ticker-label {
  color: var(--text-muted);
  font-size: 11px;
}

.ticker-sep {
  color: var(--border-color);
  margin: 0 4px;
  opacity: 0.8;
  font-size: 11px;
}

/* PC 1320px Max Container */
.lhb-max-wrap {
  max-width: 1320px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}

/* Table Card & Header & Row items */
.lhb-table-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  overflow: hidden;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
}

.table-scroll-content {
  display: inline-block;
  min-width: 100%;
  width: max-content;
  vertical-align: top;
}

.lhb-table-header,
.lhb-row-item {
  width: 100%;
  min-width: max-content;
}

.lhb-row-item > .d-flex {
  width: 100%;
  min-width: max-content;
}

.lhb-table-header {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-color);
  padding: 5px 8px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-muted);
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.12s ease;
}

.sortable:hover {
  color: var(--accent-color, #3b82f6);
}

.sort-icon-active {
  color: var(--accent-color, #3b82f6);
}

/* Rows Styling */
.lhb-row-item {
  padding: 4px 8px;
  min-height: 35px;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.1s ease;
  font-size: 12px;
}

.lhb-row-item:hover {
  background-color: var(--bg-muted);
}

.lhb-row-even {
  background-color: rgba(125, 125, 125, 0.015);
}

.lhb-row-selected {
  background-color: rgba(59, 130, 246, 0.08) !important;
}

/* Sticky Left Column (100% Solid & Persistent on Mobile Across All Columns) */
.col-sticky-name {
  position: sticky;
  left: 0;
  z-index: 5;
  background-color: var(--bg-card);
  box-shadow: 2px 0 6px -1px rgba(0, 0, 0, 0.12);
}

.lhb-table-header .col-sticky-name {
  position: sticky;
  left: 0;
  top: 0;
  z-index: 10;
  background-color: var(--bg-card);
}

.lhb-row-item:hover .col-sticky-name {
  background-color: var(--bg-muted);
}

.lhb-row-selected .col-sticky-name {
  background-color: rgba(59, 130, 246, 0.08) !important;
}

.lhb-row-even .col-sticky-name {
  background-color: var(--bg-card);
}

/* Prevent Row Wrapping on PC */
@media (min-width: 768px) {
  .seat-pills-row,
  .concepts-pills-row {
    flex-wrap: nowrap !important;
    overflow: hidden;
  }
}

/* Stock Name & Micro Badges */
.stock-name-text {
  font-size: 12.5px;
  color: var(--text-main);
  line-height: 1.25;
}

.stock-code-text {
  font-size: 10.5px;
  line-height: 1.2;
}

.badge-mini {
  font-size: 9px;
  padding: 0 3px;
  border-radius: 2px;
  font-weight: 600;
  line-height: 12px;
  display: inline-block;
  vertical-align: middle;
}

.badge-t {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.badge-3d {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

/* Seat Mini Chips */
.seat-mini-chip {
  display: inline-block;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10.5px;
  line-height: 16px;
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.seat-chip-buy {
  background: rgba(239, 68, 68, 0.09);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.22);
}

.seat-chip-sell {
  background: rgba(34, 197, 94, 0.09);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.22);
}

/* Concept Mini Pills */
.concept-mini-pill {
  display: inline-block;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10.5px;
  line-height: 16px;
  background: var(--bg-muted);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  max-width: 110px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

/* Quote Gain & Netbuy */
.quote-gain-num {
  font-size: 12.5px;
  line-height: 1.2;
}

.quote-netbuy-num {
  font-size: 12px;
  line-height: 1.2;
}

.small-val {
  font-size: 11.5px;
}

/* Ladder Mini Badges */
.ladder-mini-badge {
  display: inline-block;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  margin-top: 1px;
  white-space: nowrap;
}

.ladder-high {
  background: #ef4444;
  color: #ffffff;
}

.ladder-mid {
  background: #f87171;
  color: #ffffff;
}

.ladder-multi {
  background: rgba(239, 68, 68, 0.14);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.ladder-first {
  background: rgba(245, 158, 11, 0.14);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.ladder-rebound {
  background: rgba(139, 92, 246, 0.14);
  color: #8b5cf6;
  border: 1px solid rgba(139, 92, 246, 0.3);
}

.ladder-default {
  background: var(--bg-muted);
  color: var(--text-muted);
}

/* Ultra-Slim Scrollbar */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(150, 150, 150, 0.3);
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(150, 150, 150, 0.5);
}
</style>
